import { useState, useRef } from 'react';
import { Modal, Alert, Spin } from 'antd';
import ModalHeader from '../../../../components/atoms/ModalHeader';
import FormField from '../../../../components/FormField/FormField';
import Button from '../../../../components/atoms/Button';
import FormWrapper from '../../../../components/categories/forms/FormWrapper';
import { z } from 'zod';
import { InfoCircle } from 'iconsax-react';
import VariantsSelector from './VariantsSelector';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import {
  useUploadOperatorCategoryDocumentMutation,
  useUploadOperatorSpecialityDocumentMutation,
  useAddOperatorCategoryMutation,
} from '../../../../store/services/lookups';
import { field } from '../../../../components/FormField/interfaces';
import { message } from 'antd';

const englishRegex = /^[a-zA-Z0-9\s!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+$/;
const arabicRegex = /^[\u0600-\u06FF0-9\s!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+$/;

const getAddCategorySchema = (t: any) => z.object({
  categoryNameEn: z
    .string()
    .min(1, t('categories.validation.categoryNameRequired'))
    .regex(englishRegex, t('categories.validation.englishRegex'))
    .min(2, t('categories.validation.min2'))
    .max(100, t('categories.validation.max100')),
  categoryNameAr: z
    .string()
    .min(1, t('categories.validation.categoryNameRequired'))
    .regex(arabicRegex, t('categories.validation.arabicRegex'))
    .min(2, t('categories.validation.min2'))
    .max(100, t('categories.validation.max100')),
  categoryIcon: z
    .any()
    .refine((files) => files && files.length > 0, t('categories.validation.categoryImageRequired'))
    .refine(
      (files) => {
        if (!files || files.length === 0) return true;
        const size = files[0]?.originFileObj?.size ?? files[0]?.size;
        return !size || size <= 5 * 1024 * 1024;
      },
      t('categories.validation.maxFileSize5MB')
    ),
  subCategoryNameEn: z
    .string()
    .min(1, t('categories.validation.subCategoryNameRequired'))
    .regex(englishRegex, t('categories.validation.englishRegex'))
    .min(2, t('categories.validation.min2'))
    .max(100, t('categories.validation.max100')),
  subCategoryNameAr: z
    .string()
    .min(1, t('categories.validation.subCategoryNameRequired'))
    .regex(arabicRegex, t('categories.validation.arabicRegex'))
    .min(2, t('categories.validation.min2'))
    .max(100, t('categories.validation.max100')),
  subCategoryIcon: z
    .any()
    .refine((files) => files && files.length > 0, t('categories.validation.subCategoryImageRequired'))
    .refine(
      (files) => {
        if (!files || files.length === 0) return true;
        const size = files[0]?.originFileObj?.size ?? files[0]?.size;
        return !size || size <= 5 * 1024 * 1024;
      },
      t('categories.validation.maxFileSize5MB')
    ),
  variants: z.array(z.string()).min(1, t('categories.validation.variantRequired')),
});

/**
 * EagerFileField — lives inside FormWrapper so it has access to form context.
 * It updates the RHF field value (for display) AND triggers the upload immediately.
 */
function EagerFileField({
  name,
  uploadFn,
  onDocumentId,
  onUploadStateChange,
  ...fieldProps
}: Omit<field, 'onChangeFile'> & {
  uploadFn: (formData: FormData) => Promise<any>;
  onDocumentId: (id: string) => void;
  onUploadStateChange?: (isUploading: boolean) => void;
}) {
  const [isUploading, setIsUploading] = useState(false);
  const { setValue } = useFormContext();

  const handleChange = async (fileList: any[]) => {
    // Update the form value so Dragger shows the file
    setValue(name, fileList, { shouldValidate: true });

    const file = fileList?.[fileList.length - 1]?.originFileObj;
    if (!file) return;

    setIsUploading(true);
    onUploadStateChange?.(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await uploadFn(formData);
      const docId =
        res?.payload?.[0]?.id ||   // array-wrapped payload
        res?.payload?.id ||         // object payload
        res?.id ||                  // flat response
        '';
      onDocumentId(docId);
    } catch {
      onDocumentId('');
    } finally {
      setIsUploading(false);
      onUploadStateChange?.(false);
    }
  };

  return (
    <Spin spinning={isUploading} size="small">
      <FormField
        {...fieldProps}
        name={name}
        onChangeFile={handleChange}
      />
    </Spin>
  );
}

interface AddCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function AddCategoryModal({
  isOpen,
  onClose,
  onSuccess,
}: AddCategoryModalProps) {
  const { t } = useTranslation();
  const [addOperatorCategory, { isLoading }] = useAddOperatorCategoryMutation();

  const [uploadCategoryDocument] = useUploadOperatorCategoryDocumentMutation();
  const [uploadSpecialityDocument] = useUploadOperatorSpecialityDocumentMutation();

  const [isUploadingDocs, setIsUploadingDocs] = useState(false);

  const categoryDocumentIdRef = useRef<string>('');
  const subCategoryDocumentIdRef = useRef<string>('');

  const defaultValues = {
    categoryNameEn: '',
    categoryNameAr: '',
    categoryIcon: [],
    subCategoryNameEn: '',
    subCategoryNameAr: '',
    subCategoryIcon: [],
    variants: [],
  };

  const handleSubmit = async (data: any) => {
    try {
      const payload = {
        nameEn: data.categoryNameEn,
        nameAr: data.categoryNameAr,
        documentId: categoryDocumentIdRef.current,
        specialities: [
          {
            nameEn: data.subCategoryNameEn,
            nameAr: data.subCategoryNameAr,
            documentId: subCategoryDocumentIdRef.current,
          },
        ],
        attributeIds: data.variants,
      };

      await addOperatorCategory(payload).unwrap();
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Failed to create category:', error);
      const errorMessage =
        error?.data?.message ||
        error?.data?.errors?.default ||
        error?.data?.errors?.[0] ||
        t('categories.categoryAddFailed', 'Failed to create category');
      message.error(errorMessage);
    }
  };

  const addCategorySchema = getAddCategorySchema(t);

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={false}
      title={
        <ModalHeader
          title={t('categories.addCategory')}
          description={t('categories.addCategoryDesc')}
        />
      }
      centered
      width={600}
    >
      <FormWrapper
        schema={addCategorySchema}
        onSubmit={handleSubmit}
        values={defaultValues}
      >
        <div className="flex flex-col gap-6 mt-4">
          <div className="flex flex-col gap-4">
            <FormField
              type="text"
              name="categoryNameEn"
              id="categoryNameEn"
              label={t('categories.categoryEN')}
              placeholder={t('categories.setName')}
              className="!rounded-lg"
            />
            <FormField
              type="text"
              name="categoryNameAr"
              id="categoryNameAr"
              label={t('categories.categoryAR')}
              placeholder={t('categories.setName')}
              className="!rounded-lg"
            />
            <EagerFileField
              type="file"
              name="categoryIcon"
              id="categoryIcon"
              label={t('categories.categoryImage')}
              maxCount={1}
              multiple={false}
              accept="image/png, image/jpeg"
              uploadText={t('categories.dragDropUpload')}
              uploadHints={[
                t('categories.acceptedFileTypes'),
                t('categories.uploadOneFile'),
                t('categories.maxFileSize5MB'),
              ]}
              uploadFn={(fd: FormData) => uploadCategoryDocument(fd).unwrap()}
              onDocumentId={(id) => { categoryDocumentIdRef.current = id; }}
              onUploadStateChange={setIsUploadingDocs}
            />
            <VariantsSelector
              name="variants"
              label={t('categories.chooseVariant')}
            />
          </div>

          <Alert
            message={t('categories.newCategoryAlert')}
            type="warning"
            icon={<InfoCircle size={20} />}
            showIcon
            className="!bg-yellow-50 !border-none !text-yellow-500 rounded-lg"
          />

          <div className="flex flex-col gap-4">
            <FormField
              type="text"
              name="subCategoryNameEn"
              id="subCategoryNameEn"
              label={t('categories.subCategoryEN')}
              placeholder={t('categories.setName')}
              className="!rounded-lg"
            />
            <FormField
              type="text"
              name="subCategoryNameAr"
              id="subCategoryNameAr"
              label={t('categories.subCategoryAR')}
              placeholder={t('categories.setName')}
              className="!rounded-lg"
            />
            <EagerFileField
              type="file"
              name="subCategoryIcon"
              id="subCategoryIcon"
              label={t('categories.subCategoryImage')}
              maxCount={1}
              multiple={false}
              accept="image/png, image/jpeg"
              uploadText={t('categories.dragDropUpload')}
              uploadHints={[
                t('categories.acceptedFileTypes'),
                t('categories.uploadOneFile'),
                t('categories.maxFileSize5MB'),
              ]}
              uploadFn={(fd: FormData) => uploadSpecialityDocument(fd).unwrap()}
              onDocumentId={(id) => { subCategoryDocumentIdRef.current = id; }}
              onUploadStateChange={setIsUploadingDocs}
            />
          </div>
        </div>

        <div className="flex justify-end gap-4 mt-8 pt-4 border-t border-gray-100">
          <Button variant="outline" onClick={onClose} type="button">
            {t('common.cancel')}
          </Button>
          <Button type="submit" loading={isLoading} disabled={isUploadingDocs || isLoading}>
            {t('categories.saveCategory')}
          </Button>
        </div>
      </FormWrapper>
    </Modal>
  );
}
